import { createScriptElement, removeElements } from './tracking-provider-dom-utils'

export interface TrackingProviderConfig {
  key: string
  domain: string
  path: string | null | (() => string | null)
  async?: boolean // default to true
  defer?: boolean // default to true
  maxDelay?: number | null // default to 3100
  minDelay?: number | null
  attributes?: Record<string, string>
  injectAt?: 'head' | 'beforeFirstScript'
  init?: () => void | Promise<void>
  onLoad?: (scriptElement: HTMLScriptElement | null) => void
}

export interface TrackingProviderLoaderConfig {
  onError?: (msg: string, error: unknown) => void
}

type Task = () => void
type AsyncTask = () => Promise<void>

export function executeSafely(
  task: Task | AsyncTask,
  msg?: string,
  onError?: TrackingProviderLoaderConfig['onError'],
): AsyncTask {
  return async () => {
    try {
      await task()
    } catch (err) {
      if (typeof onError === 'function') {
        onError(msg || '', err)
      } else if (typeof window !== 'undefined') {
        const isVerbose = new URLSearchParams(window.location.search).get('logLevel') === 'verbose'
        if (isVerbose) {
          if (msg) {
            console.error(msg, err)
          } else {
            console.error(err)
          }
        }
      }
    }
  }
}

function executeOnDocumentReadyStateComplete(task: Task): Task {
  return () => {
    if (document.readyState === 'complete') {
      task()
    } else {
      const listener = () => {
        if (document.readyState === 'complete') {
          task()
          document.removeEventListener('readystatechange', listener)
        }
      }
      document.addEventListener('readystatechange', listener)
    }
  }
}

function createScriptTag(
  { key, domain, path: pathOrGetPath, async = true, defer = true, attributes, onLoad }: TrackingProviderConfig,
  { onError }: TrackingProviderLoaderConfig,
): HTMLScriptElement | null {
  const path = typeof pathOrGetPath === 'function' ? pathOrGetPath() : pathOrGetPath
  if (!(typeof path === 'string' && path.length > 0)) {
    return null
  }
  if (attributes?.id) {
    removeElements(`script#${attributes.id}`)
  }
  const script = createScriptElement(
    { async, defer },
    {
      src: domain + path,
      ...(attributes || {}),
    },
  )

  if (typeof onLoad === 'function') {
    script.onload = executeSafely(
      executeOnDocumentReadyStateComplete(() => {
        onLoad(script)
      }),
      `TrackingProviderLoader:onScriptLoad(${key})`,
      onError,
    )
  }
  return script
}

function insertBeforeFirstScript(script: HTMLScriptElement): void {
  const firstScriptTag = document.body.getElementsByTagName('script')[0]
  if (firstScriptTag?.parentNode) {
    firstScriptTag.parentNode.insertBefore(script, firstScriptTag)
  } else {
    document.body.appendChild(script)
  }
}

function createScriptAndInsertIntoDocument(config: TrackingProviderConfig, loaderConfig: TrackingProviderLoaderConfig) {
  const script = createScriptTag(config, loaderConfig)
  if (script) {
    if (config.injectAt === 'beforeFirstScript') {
      insertBeforeFirstScript(script)
    } else {
      document.body.appendChild(script)
    }
  } else if (typeof config.onLoad === 'function') {
    void executeSafely(
      executeOnDocumentReadyStateComplete(() => {
        if (config.onLoad) config.onLoad(null)
      }),
      `TrackingProviderLoader:onLoadWithoutScript(${config.key})`,
      loaderConfig.onError,
    )()
  }
}

function pageLoadPromise() {
  return new Promise(resolve => {
    if (document.readyState === 'complete') {
      resolve(null)
    } else {
      const listener = () => {
        if (document.readyState === 'complete') {
          document.removeEventListener('readystatechange', listener)
          resolve(null)
        }
      }
      document.addEventListener('readystatechange', listener)
    }
  })
}

function wait(timeout = 3100) {
  if (timeout < 0) {
    return Promise.resolve()
  }
  return new Promise(resolve => {
    setTimeout(resolve, timeout)
  })
}

/**
 * If we get minDelay, we wait for minDelay before loading the script.
 * Unless we get maxDelay as null, we do race with pageLoad
 * else we run it immediately
 */
function addScript(config: TrackingProviderConfig, loaderConfig: TrackingProviderLoaderConfig) {
  if (config.minDelay) {
    void wait(config.minDelay).then(() => {
      createScriptAndInsertIntoDocument(config, loaderConfig)
    })
  } else if (config.maxDelay !== null) {
    void Promise.race([pageLoadPromise(), wait(config.maxDelay)]).then(() => {
      createScriptAndInsertIntoDocument(config, loaderConfig)
    })
  } else {
    createScriptAndInsertIntoDocument(config, loaderConfig)
  }
}

export function loadTrackingProvider(config: TrackingProviderConfig, loaderConfig: TrackingProviderLoaderConfig = {}) {
  return executeSafely(
    async () => {
      if (typeof config.init === 'function') {
        await Promise.resolve(config.init())
      }
      if (config.path || config.onLoad) {
        addScript(config, loaderConfig)
      }
    },
    `TrackingProviderLoader:loadTrackingProvider(${config.key})`,
    loaderConfig.onError,
  )()
}
